/**
 * Insights → Feedback API. Every route is behind the Insights roles and
 * `insights.feedback_review`.
 */
import api from '@/services/api';
import { filenameFrom, triggerBlobDownload } from '@/utils/download';
import type {
  FeedbackConversation, FeedbackDetail, FeedbackFacets, FeedbackPage, FeedbackQuery,
} from '../types/feedback';

const BASE = '/insights/feedback';
// What "Open" may show in a tab: the files routes' inline-safe set plus plain
// text. Never SVG or HTML — they run script when opened as a document.
const OPENABLE_TYPES = new Set([
  'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/plain',
]);

function params(query: FeedbackQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (query.rating) p.set('rating', query.rating);
  (query.chatbotIds || []).forEach((id) => p.append('chatbot_ids[]', id));
  (query.tenantIds || []).forEach((id) => p.append('tenant_ids[]', id));
  (query.reasons || []).forEach((key) => p.append('reasons[]', key));
  if (query.hasComment === true) p.set('has_comment', 'true');
  if (query.hasComment === false) p.set('has_comment', 'false');
  if (query.dateFrom) p.set('date_from', query.dateFrom);
  if (query.dateTo) p.set('date_to', query.dateTo);
  if (query.sort) p.set('sort', query.sort);
  if (query.order) p.set('order', query.order);
  if (query.cursor) p.set('cursor', query.cursor);
  if (query.limit) p.set('limit', String(query.limit));
  return p;
}

const feedbackReviewService = {
  /** One page; pass the previous page's `next_cursor` for the next one. */
  async list(query: FeedbackQuery, signal?: AbortSignal): Promise<FeedbackPage> {
    // The search text goes in the body, never the URL (the platform's request
    // log keeps every URL); the filters and the cursor stay in the query string.
    const { data } = await api.post(`${BASE}?${params(query)}`, { q: query.q?.trim() || '' }, { signal });
    return data;
  },

  async facets(range: Pick<FeedbackQuery, 'dateFrom' | 'dateTo'>): Promise<FeedbackFacets> {
    const { data } = await api.get(`${BASE}/facets?${params(range)}`);
    return data;
  },

  /** One item and the conversation's other feedback. No message content. */
  async detail(feedbackId: string, signal?: AbortSignal): Promise<FeedbackDetail> {
    const { data } = await api.get(`${BASE}/${encodeURIComponent(feedbackId)}`, { signal });
    return data;
  },

  /** Opens the conversation for review - the window calls it once per
   *  conversation it opens. */
  async openConversation(conversationId: string): Promise<FeedbackConversation> {
    const { data } = await api.get(`${BASE}/conversations/${encodeURIComponent(conversationId)}`);
    return data;
  },

  /** A file of a reviewed conversation, through the review's own route.
   *  `open` shows it in a new tab where the browser can; otherwise
   *  it is saved. */
  async getFile(
    conversationId: string, attachmentId: string,
    opts: { version?: number | null; fallbackName: string; open?: boolean },
  ): Promise<void> {
    const p = new URLSearchParams();
    if (opts.version != null) p.set('version', String(opts.version));
    // A file too big to pass through the API comes back as a short-lived link
    // to storage instead of its bytes (the only JSON this route answers with).
    p.set('url', '1');
    const response = await api.get(
      `${BASE}/conversations/${encodeURIComponent(conversationId)}/files/${encodeURIComponent(attachmentId)}?${p}`,
      { responseType: 'blob' },
    );
    const blob = response.data as Blob;
    if (blob.type.startsWith('application/json')) {
      const { url } = JSON.parse(await blob.text()) as { url?: string };
      // A signed storage link, and only ever that: never a script URL.
      if (url && new URL(url).protocol === 'https:') {
        const a = document.createElement('a');
        a.href = url;
        // Its own tab: the review window stays where it is.
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
    }
    const name = filenameFrom(response.headers, opts.fallbackName);
    // Only types that cannot run script are opened in a tab, re-typed so the
    // browser treats the bytes as nothing else; a blob URL is our origin.
    const type = blob.type.split(';')[0].trim().toLowerCase();
    if (opts.open && OPENABLE_TYPES.has(type)) {
      const url = URL.createObjectURL(new Blob([blob], { type }));
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    triggerBlobDownload(blob, name);
  },
};

export default feedbackReviewService;
